<template>
  <component :is="component" :class="className" :id="id">
    <slot></slot>
  </component>
</template>

<script>
export default {
  name: 'PText',
  props: {
    alignment: {
      type: String,
      default: '',
    },
    as: {
      type: String,
      default: 'span',
      validator: value =>
        [
          'dt',
          'dd',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'span',
          'strong',
          'a',
          'div',
          'legend',
        ].includes(value),
    },
    breakWord: Boolean,
    tone: {
      type: String,
      validator: value =>
        [
          '',
          'success',
          'critical',
          'caution',
          'subdued',
          'text-inverse',
          'magic',
          'info',
          'magic-subdued',
        ].includes(value),
    },
    fontWeight: {
      type: String,
      validator: value => ['regular', 'medium', 'semibold', 'bold'].includes(value),
    },
    id: { type: String, default: null },
    numeric: Boolean,
    truncate: Boolean,
    variant: {
      type: String,
      validator: value =>
        [
          'headingXs',
          'headingSm',
          'headingMd',
          'headingLg',
          'headingXl',
          'heading2xl',
          'heading3xl',
          'bodyXs',
          'bodySm',
          'bodyMd',
          'bodyLg',
        ].includes(value),
      default: 'bodyMd',
    },
    visuallyHidden: Boolean,
    // textDecorationLine: {
    //   type: String,
    //   validator: value => ['line-through'].includes(value),
    // },
  },
  computed: {
    component() {
      return this.as || (this.visuallyHidden ? 'span' : 'p');
    },
    className() {
      return [
        'root',
        'ptext',
        (this.alignment || this.truncate) && 'block',
        this.breakWord && 'break',
        this.numeric && 'numeric',
        this.truncate && 'truncate',
        this.visuallyHidden && 'visuallyHidden',
        this.fontWeight,
        this.variant,
        this.tone,
      ].filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
.root {
  margin: 0;
  text-align: inherit;
}

.block {
  display: block;
}

.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.visuallyHidden {
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  @include visually-hidden;
}

.start {
  text-align: start;
}

.center {
  text-align: center;
}

.end {
  text-align: end;
}

.justify {
  text-align: justify;
}

.success {
  color: var(--p-color-text-success);
}

.critical {
  color: var(--p-color-text-critical);
}

.caution {
  color: var(--p-color-text-caution);
}

.subdued {
  color: var(--p-color-text-secondary);
}

.magic {
  color: var(--p-color-text-magic);
}

.info {
  color: var(--p-color-text-info);
}

.magic-subdued {
  color: var(--p-color-text-magic-secondary);
}

.text-inverse {
  color: var(--p-color-text-inverse);
}

.headingXs {
  font-size: var(--p-text-heading-xs-font-size);
  font-weight: var(--p-text-heading-xs-font-weight);
  letter-spacing: var(--p-text-heading-xs-font-letter-spacing);
  line-height: var(--p-text-heading-xs-font-line-height);
}

.headingSm {
  font-size: var(--p-text-heading-sm-font-size);
  font-weight: var(--p-text-heading-sm-font-weight);
  letter-spacing: var(--p-text-heading-sm-font-letter-spacing);
  line-height: var(--p-text-heading-sm-font-line-height);
}

.headingMd {
  font-size: var(--p-text-heading-md-font-size);
  font-weight: var(--p-text-heading-md-font-weight);
  letter-spacing: var(--p-text-heading-md-font-letter-spacing);
  line-height: var(--p-text-heading-md-font-line-height);
}

.headingLg {
  font-size: var(--p-text-heading-lg-font-size);
  font-weight: var(--p-text-heading-lg-font-weight);
  letter-spacing: var(--p-text-heading-lg-font-letter-spacing);
  line-height: var(--p-text-heading-lg-font-line-height);
}

.headingXl {
  font-size: var(--p-font-size-500);
  font-weight: var(--p-font-weight-semibold);
  letter-spacing: var(--p-font-letter-spacing-dense);
  line-height: var(--p-font-line-height-600);

  @media (--p-breakpoints-md-up) {
    font-size: var(--p-text-heading-xl-font-size);
    font-weight: var(--p-text-heading-xl-font-weight);
    letter-spacing: var(--p-text-heading-xl-font-letter-spacing);
    line-height: var(--p-text-heading-xl-font-line-height);
  }
}

.heading2xl {
  font-size: var(--p-font-size-600);
  font-weight: var(--p-font-weight-bold);
  letter-spacing: var(--p-font-letter-spacing-dense);
  line-height: var(--p-font-line-height-800);

  @media (--p-breakpoints-md-up) {
    font-size: var(--p-text-heading-2xl-font-size);
    font-weight: var(--p-text-heading-2xl-font-weight);
    letter-spacing: var(--p-text-heading-2xl-font-letter-spacing);
    line-height: var(--p-text-heading-2xl-font-line-height);
  }
}

.heading3xl {
  font-size: var(--p-font-size-750);
  font-weight: var(--p-font-weight-bold);
  letter-spacing: var(--p-font-letter-spacing-denser);
  line-height: var(--p-font-line-height-1000);

  @media (--p-breakpoints-md-up) {
    font-size: var(--p-text-heading-3xl-font-size);
    font-weight: var(--p-text-heading-3xl-font-weight);
    letter-spacing: var(--p-text-heading-3xl-font-letter-spacing);
    line-height: var(--p-text-heading-3xl-font-line-height);
  }
}

.bodyXs {
  font-size: var(--p-text-body-xs-font-size);
  font-weight: var(--p-text-body-xs-font-weight);
  letter-spacing: var(--p-text-body-xs-font-letter-spacing);
  line-height: var(--p-text-body-xs-font-line-height);
}

.bodySm {
  font-size: var(--p-text-body-sm-font-size);
  font-weight: var(--p-text-body-sm-font-weight);
  letter-spacing: var(--p-text-body-sm-font-letter-spacing);
  line-height: var(--p-text-body-sm-font-line-height);
}

.bodyMd {
  font-size: var(--p-text-body-md-font-size);
  font-weight: var(--p-text-body-sm-font-weight);
  letter-spacing: var(--p-text-body-md-font-letter-spacing);
  line-height: var(--p-text-body-md-font-line-height);
}

.bodyLg {
  font-size: var(--p-text-body-lg-font-size);
  font-weight: var(--p-text-body-sm-font-weight);
  letter-spacing: var(--p-text-body-lg-font-letter-spacing);
  line-height: var(--p-text-body-lg-font-line-height);
}

// font-weight must be below variant styles so
// it can override the styles when necessary.
.regular {
  font-weight: var(--p-font-weight-regular);
}

.medium {
  font-weight: var(--p-font-weight-medium);
}

.semibold {
  font-weight: var(--p-font-weight-semibold);
}

.bold {
  font-weight: var(--p-font-weight-bold);
}

.break {
  overflow-wrap: anywhere;
  word-break: normal;
}

.numeric {
  font-variant-numeric: tabular-nums lining-nums;
}

.line-through {
  text-decoration-line: line-through;
}

.ptext::v-deep a {
  color: inherit;
  text-decoration-color: inherit;
}
</style>
